import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    // Navigation
    dashboard: 'Dashboard',
    games: 'Games',
    leaderboard: 'Leaderboard',
    adminDashboard: 'Admin Dashboard',
    manageTeams: 'Manage Teams',
    manageUsers: 'Manage Users',
    manageGames: 'Manage Games',
    manageCompetitions: 'Competitions',
    logout: 'Logout',
    welcome: 'Welcome',
    
    // Auth
    login: 'Login',
    register: 'Register',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    fullName: 'Full Name',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: 'Already have an account?',
    
    // Dashboard
    totalGames: 'Total Games',
    activeBets: 'Active Bets',
    totalPoints: 'Total Points',
    currentRank: 'Current Rank',
    recentBets: 'Recent Bets',
    upcomingGames: 'Upcoming Games',
    vs: 'VS',
    viewAllGames: 'View All Games',
    viewLeaderboard: 'View Leaderboard',
    finishedCardFinal: 'Final',
    finishedCardYou: 'You',
    finishedCardPts: 'Pts',
    tierExact: 'Exact',
    tierOutcome: 'Right winner',
    tierWrong: 'Miss',
    tierPending: 'Pending',

    // Games
    allGames: 'All Games',
    placeBet: 'Place Bet',
    homeScore: 'Home Score',
    awayScore: 'Away Score',
    submitBet: 'Submit Bet',
    cancel: 'Cancel',
    upcoming: 'Upcoming',
    live: 'Live',
    finished: 'Finished',
    bettingLocked: 'Betting Locked',
    finalResult: 'Final Result',
    yourBet: 'Your Bet',
    points: 'Points',
    showUserBets: 'Show User Bets',
    userBets: 'User Bets',
    noBetsPlaced: 'No bets placed for this game yet',
    exactScore: 'Exact Score',
    correctWinner: 'Correct Winner',
    incorrect: 'Incorrect',
    
    // Leaderboard
    rank: 'Rank',
    player: 'Player',
    totalBets: 'Total Bets',
    correctPredictions: 'Correct Predictions',
    accuracy: 'Accuracy',
    seasonStats: 'Season Stats',
    totalPlayers: 'Total Players',
    highestScore: 'Highest Score',
    bets: 'Bets',
    champion: 'Champion',
    
    // Admin Dashboard
    overview: 'Overview',
    totalUsers: 'Total Users',
    totalTeams: 'Total Teams',
    recentActivity: 'Recent Activity',
    quickActions: 'Quick Actions',
    addNewGame: 'Add New Game',
    addNewTeam: 'Add New Team',
    viewAllUsers: 'View All Users',
    
    // Admin Teams
    manageTeamsTitle: 'Manage Teams',
    importCSV: 'Import CSV',
    sampleCSV: 'Sample CSV',
    addTeam: 'Add Team',
    teamName: 'Team Name',
    logoURL: 'Logo URL',
    description: 'Description',
    createTeam: 'Create Team',
    updateTeam: 'Update Team',
    editTeam: 'Edit Team',
    createNewTeam: 'Create New Team',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    csvImportFormat: 'CSV Import Format',
    requiredColumns: 'Required columns',
    optionalColumns: 'Optional columns',
    example: 'Example',
    noTeamsAvailable: 'No teams available',
    createFirstTeam: 'Create your first team to get started!',
    teamManagementGuide: 'Team Management Guide',
    addTeamsDesc: 'Create new football teams with names, logos, and descriptions',
    importCsvDesc: 'Upload multiple teams at once using CSV format',
    manageGamesDesc: 'Use teams to create games and manage the betting schedule',
    competitionsAdminHelp:
      'Create and edit competitions. Enter both Hebrew and English names. The slug is used in URLs (/c/your-slug/...). You cannot delete a competition that still has games.',
    compSlug: 'URL slug',
    compStart: 'Start date',
    compEnd: 'End date',
    compType: 'Type',
    compLeague: 'League',
    compOther: 'Other',
    compActive: 'Active',
    compDefault: 'Default competition',
    compAdminBettingLocked: 'Lock betting (admin)',
    addCompetition: 'Add competition',
    competitionName: 'Competition name',
    competitionNameHe: 'Name (Hebrew)',
    competitionNameEn: 'Name (English)',

    // Admin Games
    manageGamesTitle: 'Manage Games',
    addGame: 'Add Game',
    homeTeam: 'Home Team',
    awayTeam: 'Away Team',
    date: 'Date',
    time: 'Hour:min',
    time24hNote: '(24-hour)',
    timeHourPlaceholder: 'HH',
    timeMinPlaceholder: 'mm',
    timeMinAria: 'Minutes',
    timeFieldHelp: 'Hour:min in 24h form (00:00 – 23:59)',
    timeCsvFormatHint: 'Hour:min column uses HH:mm (e.g. 20:00)',
    round: 'Round',
    createGame: 'Create Game',
    updateGame: 'Update Game',
    editGame: 'Edit Game',
    createNewGame: 'Create New Game',
    selectHomeTeam: 'Select home team',
    selectAwayTeam: 'Select away team',
    gameFinished: 'Game Finished',
    dateFormat: 'Date format',
    timeFormat: 'Time (Hour:min)',
    noGamesAvailable: 'No games available',
    createFirstGame: 'Create your first game to get started!',
    homeTeamScore: 'Home Team Score',
    awayTeamScore: 'Away Team Score',
    
    // Common
    loading: 'Loading',
    close: 'Close',
    submit: 'Submit',
    update: 'Update',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    all: 'All',
    today: 'Today',
    tomorrow: 'Tomorrow',
    thisWeek: 'This Week',
    language: 'Language',
    english: 'English',
    hebrew: 'עברית',
    csvImportGuide: 'CSV Import Guide',
    downloadSample: 'Download Sample',
    addRound: 'Add Round',
    addRoundDesc: 'Create 7 games at once for a round',
    closeRound: 'Close Round',
    closeRoundDesc: 'Update scores for all games in a round',
    roundName: 'Round Name',
    gameNumber: 'Game {number}',
    selectRound: 'Select Round',
    updateAllScores: 'Update All Scores',
    roundGames: 'Round Games',
    noGamesInRound: 'No games found in this round',
    allGamesCreated: 'All games created successfully!',
    allScoresUpdated: 'All scores updated successfully!',
    cancelGame: 'Cancel Game',
    cancelGameDesc: 'Game scores will be saved but no points will be awarded',
    gameCanceled: 'Game Canceled',
    canceled: 'Canceled'
  },
  he: {
    // Navigation
    dashboard: 'לוח בקרה',
    games: 'משחקים',
    leaderboard: 'לוח מובילים',
    adminDashboard: 'לוח בקרה מנהל',
    manageTeams: 'ניהול קבוצות',
    manageUsers: 'ניהול משתמשים',
    manageGames: 'ניהול משחקים',
    manageCompetitions: 'תחרויות',
    logout: 'התנתקות',
    welcome: 'ברוכים הבאים',
    
    // Auth
    login: 'כניסה',
    register: 'הרשמה',
    email: 'אימייל',
    password: 'סיסמה',
    confirmPassword: 'אישור סיסמה',
    fullName: 'שם מלא',
    signIn: 'היכנס',
    signUp: 'הירשם',
    dontHaveAccount: 'אין לך חשבון?',
    alreadyHaveAccount: 'יש לך כבר חשבון?',
    
    // Dashboard
    totalGames: 'סך משחקים',
    activeBets: 'הימורים פעילים',
    totalPoints: 'סך נקודות',
    currentRank: 'דירוג נוכחי',
    recentBets: 'הימורים אחרונים',
    upcomingGames: 'משחקים קרובים',
    vs: 'נגד',
    viewAllGames: 'צפה בכל המשחקים',
    viewLeaderboard: 'צפה בלוח המובילים',
    finishedCardFinal: 'סופי',
    finishedCardYou: 'אתה',
    finishedCardPts: 'נק׳',
    tierExact: 'מדויק',
    tierOutcome: 'כיוון נכון',
    tierWrong: 'פספס',
    tierPending: 'ממתין',

    // Games
    allGames: 'כל המשחקים',
    placeBet: 'הימור',
    homeScore: 'תוצאה בית',
    awayScore: 'תוצאה חוץ',
    submitBet: 'שלח הימור',
    cancel: 'ביטול',
    upcoming: 'קרוב',
    live: 'בשידור חי',
    finished: 'הסתיים',
    bettingLocked: 'הימורים נעולים',
    finalResult: 'תוצאה סופית',
    yourBet: 'ההימור שלך',
    points: 'נקודות',
    showUserBets: 'הצג הימורי משתמשים',
    userBets: 'הימורי משתמשים',
    noBetsPlaced: 'עדיין לא הוצבו הימורים למשחק זה',
    exactScore: 'תוצאה מדויקת',
    correctWinner: 'מנצח נכון',
    incorrect: 'שגוי',
    
    // Leaderboard
    rank: 'דירוג',
    player: 'שחקן',
    totalBets: 'סך הימורים',
    correctPredictions: 'ניבויים נכונים',
    accuracy: 'דיוק',
    seasonStats: 'נתוני עונה',
    totalPlayers: 'סך שחקנים',
    highestScore: 'ניקוד גבוה',
    bets: 'הימורים',
    champion: 'אלוף',
    
    // Admin Dashboard
    overview: 'סקירה כללית',
    totalUsers: 'סך משתמשים',
    totalTeams: 'סך קבוצות',
    recentActivity: 'פעילות אחרונה',
    quickActions: 'פעולות מהירות',
    addNewGame: 'הוסף משחק חדש',
    addNewTeam: 'הוסף קבוצה חדשה',
    viewAllUsers: 'צפה בכל המשתמשים',
    
    // Admin Teams
    manageTeamsTitle: 'ניהול קבוצות',
    importCSV: 'ייבוא CSV',
    sampleCSV: 'דוגמת CSV',
    addTeam: 'הוסף קבוצה',
    teamName: 'שם קבוצה',
    logoURL: 'כתובת לוגו',
    description: 'תיאור',
    createTeam: 'צור קבוצה',
    updateTeam: 'עדכן קבוצה',
    editTeam: 'ערוך קבוצה',
    createNewTeam: 'צור קבוצה חדשה',
    edit: 'ערוך',
    delete: 'מחק',
    save: 'שמור',
    csvImportFormat: 'פורמט ייבוא CSV',
    requiredColumns: 'עמודות נדרשות',
    optionalColumns: 'עמודות אופציונליות',
    example: 'דוגמה',
    noTeamsAvailable: 'אין קבוצות זמינות',
    createFirstTeam: 'צור את הקבוצה הראשונה שלך כדי להתחיל!',
    teamManagementGuide: 'מדריך ניהול קבוצות',
    addTeamsDesc: 'צור קבוצות כדורגל חדשות עם שמות, לוגואים ותיאורים',
    importCsvDesc: 'העלה מספר קבוצות בבת אחת באמצעות פורמט CSV',
    manageGamesDesc: 'השתמש בקבוצות כדי ליצור משחקים ולנהל את לוח ההימורים',
    competitionsAdminHelp:
      'צור וערוך תחרויות. יש למלא שם בעברית ושם באנגלית. המזהה (slug) מופיע בכתובת (/c/your-slug/...). לא ניתן למחוק תחרות שעדיין יש בה משחקים.',
    compSlug: 'מזהה בכתובת (slug)',
    compStart: 'תאריך התחלה',
    compEnd: 'תאריך סיום',
    compType: 'סוג',
    compLeague: 'ליגה',
    compOther: 'אחר',
    compActive: 'פעיל',
    compDefault: 'תחרות ברירת מחדל',
    compAdminBettingLocked: 'נעילת הימורים (מנהל)',
    addCompetition: 'הוסף תחרות',
    competitionName: 'שם התחרות',
    competitionNameHe: 'שם (עברית)',
    competitionNameEn: 'שם (אנגלית)',

    // Admin Games
    manageGamesTitle: 'ניהול משחקים',
    addGame: 'הוסף משחק',
    homeTeam: 'קבוצת בית',
    awayTeam: 'קבוצת חוץ',
    date: 'תאריך',
    time: 'שעה:דקות',
    time24hNote: '(פורמט 24 שעות)',
    timeHourPlaceholder: 'שעה',
    timeMinPlaceholder: 'דקה',
    timeMinAria: 'דקות',
    timeFieldHelp: 'שעה:דקות בפורמט 24 שעות (00:00 – 23:59)',
    timeCsvFormatHint: 'עמודת זמן בפורמט HH:mm (למשל 20:00)',
    round: 'סיבוב',
    createGame: 'צור משחק',
    updateGame: 'עדכן משחק',
    editGame: 'ערוך משחק',
    createNewGame: 'צור משחק חדש',
    selectHomeTeam: 'בחר קבוצת בית',
    selectAwayTeam: 'בחר קבוצת חוץ',
    gameFinished: 'משחק הסתיים',
    dateFormat: 'פורמט תאריך',
    timeFormat: 'זמן (שעה:דקות)',
    noGamesAvailable: 'אין משחקים זמינים',
    createFirstGame: 'צור את המשחק הראשון שלך כדי להתחיל!',
    homeTeamScore: 'תוצאת קבוצת בית',
    awayTeamScore: 'תוצאת קבוצת חוץ',
    
    // Common
    loading: 'טוען',
    close: 'סגור',
    submit: 'שלח',
    update: 'עדכן',
    create: 'צור',
    search: 'חיפוש',
    filter: 'סינון',
    all: 'הכל',
    today: 'היום',
    tomorrow: 'מחר',
    thisWeek: 'השבוע',
    language: 'שפה',
    english: 'English',
    hebrew: 'עברית',
    csvImportGuide: 'מדריך יבוא CSV',
    downloadSample: 'הורד דוגמה',
    addRound: 'הוסף סיבוב',
    addRoundDesc: 'צור 7 משחקים בבת אחת לסיבוב',
    closeRound: 'סגור סיבוב',
    closeRoundDesc: 'עדכן תוצאות לכל המשחקים בסיבוב',
    roundName: 'שם סיבוב',
    gameNumber: 'משחק {number}',
    selectRound: 'בחר סיבוב',
    updateAllScores: 'עדכן את כל התוצאות',
    roundGames: 'משחקי הסיבוב',
    noGamesInRound: 'לא נמצאו משחקים בסיבוב זה',
    allGamesCreated: 'כל המשחקים נוצרו בהצלחה!',
    allScoresUpdated: 'כל התוצאות עודכנו בהצלחה!',
    cancelGame: 'בטל משחק',
    cancelGameDesc: 'התוצאות יישמרו אך לא יחולקו נקודות',
    gameCanceled: 'משחק בוטל',
    canceled: 'בוטל'
  }
};

export const LanguageProvider = ({ children }) => {
  // Load saved language from localStorage or default to 'en'
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('bettingApp_language');
    return savedLanguage || 'en';
  });
  
  const [isRTL, setIsRTL] = useState(() => {
    const savedLanguage = localStorage.getItem('bettingApp_language');
    return savedLanguage === 'he';
  });

  // Set initial document direction based on saved language
  useEffect(() => {
    document.documentElement.dir = language === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    const newLanguage = language === 'en' ? 'he' : 'en';
    setLanguage(newLanguage);
    setIsRTL(newLanguage === 'he');
    
    // Save language preference to localStorage
    localStorage.setItem('bettingApp_language', newLanguage);
    
    // Update document direction
    document.documentElement.dir = newLanguage === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLanguage;
  };

  const t = (key) => {
    return translations[language][key] || key;
  };

  const value = {
    language,
    isRTL,
    toggleLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 